import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTran } from "@/hooks";

import { useAuthStore } from "@/features/auth";
import { showToast } from "@/lib/utils/toast";
import { useRolesPermissionsStore } from "./useRolesPermissionsStore";
import { useOrganizationStore } from "@/features/organization";
import { useCompanyStore } from "@/features/company";
import { useUserStore } from "./useUserStore";
import {
  filterModulesByAudience,
  formatRoleLabel,
} from "../utils/roles-permissions";
import {
  buildRoleOptions,
  resolveModuleAudience,
} from "../utils/user-role-options";
import { useNavigate } from "react-router-dom";
import { PATHS } from "@/lib/router";
import { SYSTEM_ROLES } from "@/helpers/permissionConstants";

const ADMIN_ROLES = ["system_admin", "business_unit"];
const STATUS_OPTIONS = ["active", "inactive", "deleted"];
const USERNAME_ALLOWED_REGEX = /^[a-z0-9]+$/;

const hasPermissionOverrideSelection = (permissionOverrides) => {
  if (permissionOverrides?.mode !== "override") return true;

  const allow = Array.isArray(permissionOverrides?.allow)
    ? permissionOverrides.allow
    : [];
  const deny = Array.isArray(permissionOverrides?.deny)
    ? permissionOverrides.deny
    : [];

  return allow.length > 0 || deny.length > 0;
};

export const useManageUserPageData = ({ userId }) => {
  const { t } = useTran();
  const { userRole, user } = useAuthStore();
  const {
    loadManagedUser,
    getEditForm,
    getEditMeta,
    isLoading,
    isSaving,
    getError,
    updateEditField,
    setEditFields,
    updateManagedUser,
    unlockUser,
    invalidateUser,
    getUser,
    hasEditChanges,
    hasPermissionModeChanged,
    setEditPermissionOverrides,
    sendingPasswordReset,
    sendPasswordReset,
    unlockingUserId,
  } = useUserStore();
  const {
    roles,
    permissionsModules,
    loadingModules,
    loadingRoles,
    fetchRoleList,
    fetchPermissionModules,
  } = useRolesPermissionsStore();
  const [reloadKey, setReloadKey] = useState(0);
  const orgLoadedRef = useRef(false);
  const {
    tree: organizationTree,
    loading: organizationLoading,
    fetchHierarchy,
  } = useOrganizationStore();
  const {
    companyOptions,
    companiesLoading,
    companiesLoaded,
    fetchPublicCompanies,
  } = useCompanyStore();
  const companiesLoadedRef = useRef(false);

  const isCompanyAdmin = ADMIN_ROLES.includes(userRole);
  const companyId = user?.companyId;

  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) return;

    let isActive = true;
    const fetchDetails = async () => {
      try {
        await loadManagedUser(userId, { force: reloadKey > 0 });
      } catch {
        if (!isActive) return;
      }
    };

    fetchDetails();

    return () => {
      isActive = false;
    };
  }, [loadManagedUser, reloadKey, userId]);

  useEffect(() => {
    fetchRoleList(userId);
  }, [fetchRoleList, userId]);

  useEffect(() => {
    if (!permissionsModules.length && !loadingModules) {
      fetchPermissionModules({ audience: "all" });
    }
  }, [fetchPermissionModules, loadingModules, permissionsModules.length]);

  useEffect(() => {
    if (orgLoadedRef.current) return;
    if (organizationTree.length > 0) {
      orgLoadedRef.current = true;
      return;
    }
    orgLoadedRef.current = true;
    fetchHierarchy().catch(() => {});
  }, [fetchHierarchy, organizationTree.length]);

  useEffect(() => {
    if (companiesLoadedRef.current) return;
    if (companiesLoaded || companyOptions.length > 0) {
      companiesLoadedRef.current = true;
      return;
    }
    companiesLoadedRef.current = true;
    fetchPublicCompanies().catch(() => {});
  }, [companiesLoaded, companyOptions.length, fetchPublicCompanies]);

  const editForm = getEditForm(userId);
  const meta = getEditMeta(userId);
  const storedUser = userId ? getUser(userId) : null;
  const loading = Boolean(userId && isLoading(userId));
  const saving = Boolean(userId && isSaving(userId));
  const errorMessage = userId ? getError(userId) : "";
  const hasChanges = Boolean(
    userId && (hasEditChanges(userId) || hasPermissionModeChanged(userId)),
  );

  const displayLabel =
    editForm.username ||
    editForm.email ||
    storedUser?.username ||
    storedUser?.email ||
    "";

  const roleOptions = useMemo(
    () =>
      buildRoleOptions({
        roles,
        companyId,
        currentRoleKey: userRole,
        selectedRole: editForm.role,
        t,
      }),
    [companyId, editForm.role, roles, t, userRole],
  );

  const moduleAudience = useMemo(
    () =>
      resolveModuleAudience({
        roleValue: editForm.role,
        roles,
        isCompanyAdmin,
      }),
    [editForm.role, isCompanyAdmin, roles],
  );

  const permissionModules = useMemo(
    () => filterModulesByAudience(permissionsModules, moduleAudience),
    [moduleAudience, permissionsModules],
  );

  const companyLoading = companiesLoading;

  const formatStatusLabel = useCallback(
    (value) => {
      if (!value) return "";
      if (value === "active") return t("Active");
      if (value === "inactive") return t("Disabled");
      if (value === "deleted") return t("Deleted");
      return formatRoleLabel(value);
    },
    [t],
  );

  const statusOptions = useMemo(
    () =>
      STATUS_OPTIONS.map((value) => ({
        value,
        label: formatStatusLabel(value),
      })),
    [formatStatusLabel],
  );

  const statusValue = editForm.status || storedUser?.status || "";
  const statusLabel = statusValue
    ? formatStatusLabel(statusValue)
    : t("Not set");
  const statusVariant = ["active", "verified"].includes(statusValue)
    ? "default"
    : "outline";
  const usernameValue = editForm.username || "";
  const usernameError =
    usernameValue && !USERNAME_ALLOWED_REGEX.test(usernameValue)
      ? t(
          "Username can only contain lowercase letters and numbers without spaces.",
        )
      : "";

  const formatDateTime = useCallback(
    (value) => {
      if (!value) return t("Not available");
      const date = typeof value === "string" ? new Date(value) : value;
      if (Number.isNaN(date.getTime())) return t("Not available");
      return date.toLocaleString();
    },
    [t],
  );

  const handleRetry = () => {
    if (!userId) return;
    invalidateUser(userId);
    setReloadKey((prev) => prev + 1);
  };

  const handleChange = (field) => (event) => {
    if (!userId) return;
    updateEditField(userId, field, event.target.value);
  };

  const handleSelectChange = (field) => (input) => {
    if (!userId) return;
    const value = input?.target ? input.target.value : input;
    updateEditField(userId, field, value);
  };

  // Orginal code

  // const handleOrganizationChange = (value, label) => {
  //   if (!userId) return;
  //   setEditFields(userId, {
  //     organizationUnitId: value || "",
  //     organizationUnitName: label || editForm.organizationUnitName || "",
  //   });
  // };

  const handleCompanyChange = (input) => {
    if (!userId) return;
    const value = input?.target ? input.target.value : input;
    setEditFields(userId, {
      companyId: value || "",
      organizationUnitId: [],
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!userId || saving || !hasChanges) return;
    if (usernameError) {
      showToast.error(usernameError);
      return;
    }
    if (!hasPermissionOverrideSelection(editForm.permissionOverrides)) {
      showToast.error(
        t("Select at least one permission when mode is Override."),
      );
      return;
    }

    const trimmedCompanyId = editForm.companyId?.trim();
    // const trimmedOrganizationId = editForm.organizationUnitId?.trim();
    const isCustomRole =
      !ADMIN_ROLES.includes(editForm.role) &&
      !SYSTEM_ROLES.includes(editForm.role);

    const payload = {
      name: editForm.name,
      username: editForm.username,
      email: editForm.email,
      role: editForm.role,
      customRoleName:
        editForm.role === "super_admin" ? editForm.customRoleName : undefined,
      status: editForm.status,
      organizationUnitId:
        isCompanyAdmin && !isCustomRole ? null : editForm.organizationUnitId,
      assignedToParentCompany: isCompanyAdmin
        ? undefined
        : editForm.assignedToParentCompany,
      companyId:
        isCompanyAdmin && !isCustomRole
          ? trimmedCompanyId || undefined
          : undefined,
      permissionOverrides: editForm.permissionOverrides,
    };
    try {
      await updateManagedUser(userId, payload);
      navigate(PATHS.USER_LIST);
    } catch {
      return;
    }
  };

  const handlePermissionOverridesChange = (nextOverrides) => {
    if (!userId) return;
    setEditPermissionOverrides(userId, nextOverrides);
  };

  const unlocking = Boolean(userId && unlockingUserId === userId);

  const handleUnlock = async () => {
    if (!userId || unlocking) return;
    await unlockUser(userId);
  };

  const handlePasswordReset = async () => {
    const email = editForm.email || storedUser?.email || "";
    await sendPasswordReset(email, { showToast: true, t });
  };

  const canUpdate = () => {
    const organizationUnitIds = Array.isArray(editForm.organizationUnitId)
      ? editForm.organizationUnitId
      : editForm.organizationUnitId
        ? [editForm.organizationUnitId]
        : [];
    const hasOrganizationAccess = isCompanyAdmin
      ? Boolean(String(editForm.companyId || "").trim())
      : organizationUnitIds.length > 0 || editForm.assignedToParentCompany;

    return Boolean(
      String(editForm.name || "").trim() &&
      String(editForm.username || "").trim() &&
      !usernameError &&
      String(editForm.email || "").trim() &&
      String(editForm.role || "").trim() &&
      hasOrganizationAccess,
    );
  };

  return {
    updateEditField,
    editForm,
    meta,
    loading,
    saving,
    errorMessage,
    displayLabel,
    roleOptions,
    loadingRoles,
    statusOptions,
    statusValue,
    statusLabel,
    statusVariant,
    usernameError,
    permissionModules,
    companyOptions,
    companyLoading,
    organizationTree,
    organizationLoading,
    unlocking,
    isCompanyAdmin,
    sendingReset: sendingPasswordReset,
    formatDateTime,
    handleRetry,
    handleChange,
    handleSelectChange,
    handleCompanyChange,
    handleSubmit,
    handleUnlock,
    handlePasswordReset,
    handlePermissionOverridesChange,
    canUpdate,
    hasChanges,
  };
};
